import Button from '@ets-global/b2c-website-ui/Button';
import findKey from 'lodash/findKey';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { TestTypeProgram } from '../../../constants';
import { AuthContext } from '../../../store/Auth';
import AddOrUpdateCandidateForm from './Candidate/Form';
import CandidateWidget from './Candidate/Widget';
import { useCandidateValidator } from './useCandidateValidator';

const Candidates = ({
  candidates,
  session,
  productsAndServices,
  canAddCandidates,
  sessionId,
  registrationId,
  promoCodeOrVoucher,
  setPurchaseCandidates,
  loading,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const testType = session.size ? session.get('testType') : null;
  const withPicture = session.size ? session.get('testType').get('isPhotoRequired') : false;

  const withDateOfBirth = session.size ? session.get('testType').get('isDateOfBirthRequired') : false;
  //Display candidate form from summary update
  let formKeyDisplayedInitial = false;
  if (location.state && location.state.identificationId) {
    formKeyDisplayedInitial = parseInt(
      findKey(candidates, (candidate) => candidate.identificationId === location.state.identificationId),
      10
    );
  }

  const { isValid: candidateIsValid } = useCandidateValidator(withDateOfBirth);
  const [formKeyDisplayed, setFormKeyDisplayed] = useState(formKeyDisplayedInitial);

  //Init candidate form with user info if there is no candidate register in this purchase
  const candidatesCount = candidates.length;
  let candidate = undefined;
  if (candidatesCount === 0 && user?.email) {
    candidate = {
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
    };
  }

  const handleSetPurchaseCandidatesCallback = (result) => {
    if (result) {
      setFormKeyDisplayed(false);
    }
  };

  useEffect(() => {
    let [firstCandidate] = candidates;
    if (!registrationId && firstCandidate) {
      if (!candidateIsValid(firstCandidate)) {
        setFormKeyDisplayed(0);

        return;
      }
      setPurchaseCandidates({ candidates });
    }
  }, []);

  let candidatesList = '';
  if (candidates) {
    candidatesList = candidates.map((candidate, key) => (
      <CandidateWidget
        key={key}
        order={key}
        candidate={candidate}
        hasVoucher={Boolean(promoCodeOrVoucher?.isVoucher && key === 0)}
        notEditable={promoCodeOrVoucher?.isCpf}
        showForm={formKeyDisplayed === key}
        onOpen={() => setFormKeyDisplayed(key)}
        onClose={() => setFormKeyDisplayed(false)}
        onEdit={(candidate) =>
          setPurchaseCandidates(
            {
              registrationId: registrationId || null,
              candidates: [
                ...candidates.map((currentCandidate) => {
                  if (currentCandidate.identificationId === candidate.identificationId) {
                    return candidate;
                  }

                  return currentCandidate;
                }),
              ],
            },
            handleSetPurchaseCandidatesCallback
          )
        }
        onDelete={(candidate) =>
          setPurchaseCandidates(
            {
              registrationId: registrationId || null,
              candidates: [
                ...candidates.filter(
                  (currentCandidate) => currentCandidate.identificationId !== candidate.identificationId
                ),
              ],
            },
            handleSetPurchaseCandidatesCallback
          )
        }
        sessionId={sessionId}
        withPicture={withPicture}
        withDateOfBirth={withDateOfBirth}
        productsAndServices={productsAndServices}
        loading={loading}
        testType={testType}
      />
    ));
  }

  let addCandidate = '';
  if (formKeyDisplayed === -1 || candidatesCount === 0) {
    addCandidate = (
      <AddOrUpdateCandidateForm
        candidate={candidate}
        order={candidatesCount + 1}
        notEditable={promoCodeOrVoucher?.isCpf}
        onComplete={(candidate) =>
          setPurchaseCandidates(
            {
              registrationId: registrationId || null,
              candidates: [...candidates, candidate],
            },
            handleSetPurchaseCandidatesCallback
          )
        }
        onCancel={() => {
          if (!loading) {
            setFormKeyDisplayed(false);
          }
        }}
        noCandidateAdded={candidatesCount === 0}
        sessionId={sessionId}
        testType={testType}
        productsAndServices={productsAndServices}
        withPicture={withPicture}
        withDateOfBirth={withDateOfBirth}
        loading={loading}
      />
    );
  } else if (!promoCodeOrVoucher?.isCpf && formKeyDisplayed === false && candidatesCount > 0 && canAddCandidates) {
    if (candidatesCount < session.get('seatsAvailable')) {
      addCandidate = (
        <Button
          disabled={loading}
          className={'button--dotted'}
          onClick={() => setFormKeyDisplayed(-1)}
          data-cypress="buying-process.new-candidate"
          aria-label={t('session-registration.candidate.add')}
        >
          <Trans>session-registration.candidate.add</Trans>
        </Button>
      );
    } else {
      addCandidate = (
        <div className={'session--full'}>
          <Trans>session-registration.session.full</Trans>
        </div>
      );
    }
  }

  return (
    <>
      {candidatesList}
      {addCandidate}
    </>
  );
};

Candidates.propTypes = {
  candidates: PropTypes.arrayOf(
    PropTypes.shape({
      token: PropTypes.string,
      firstname: PropTypes.string.isRequired,
      lastname: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      dateOfBirth: PropTypes.string,
      phoneNumber: PropTypes.string,
    }).isRequired
  ),
  session: PropTypes.object,
  productsAndServices: PropTypes.shape({
    correctionProductsAndServices: PropTypes.array,
    certificateProductsAndServices: PropTypes.array,
    discountProductsAndServices: PropTypes.array,
  }),
  canAddCandidates: PropTypes.bool,
  sessionId: PropTypes.number.isRequired,
  registrationId: PropTypes.string,
  promoCodeOrVoucher: PropTypes.shape({
    discountCode: PropTypes.string,
    testTypeIds: PropTypes.arrayOf(PropTypes.number),
    prepToolIds: PropTypes.arrayOf(PropTypes.number),
    isCpf: PropTypes.bool,
    referrer: PropTypes.string,
    isVoucher: PropTypes.bool,
    productAndServiceIds: PropTypes.arrayOf(PropTypes.number),
    candidate: PropTypes.shape({
      lastname: PropTypes.string,
      firstname: PropTypes.string,
      email: PropTypes.string,
      dateOfBirth: PropTypes.string,
      productsAndServices: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          quantity: PropTypes.number,
        })
      ),
    }),
  }),
  setPurchaseCandidates: PropTypes.func,
  loading: PropTypes.bool,
};

export default Candidates;
